import {Button} from "antd";
import React, {useEffect, useRef, useState} from "react";
import CollapsableBox from "../collapsableBox/CollapsableBox";
import FlashyLogo from "../../assets/images/flashy-logo-normalized.png";
import KlaviyoLogo from "../../assets/images/klaviyo-logo-normalized.png";
import MailchimpLogo from "../../assets/images/mailchimp-logo-normalized.png";
import "./consent-integration-section.css";
import ConsentIntegrationEditModal from "./ConsentIntegrationEditModal";
import useAppContext from "../../hooks/useAppContext";
import {consentIntegrationCheck} from "../../api/consentConnectionCheck";
import useUserContext from "../../hooks/useUserContext";
import {consentMailchimpListCheck} from "../../api/consentMailchimpListCheck";
import {DisconnectOutlined, LinkOutlined} from "@ant-design/icons";
import Loader from "../loader/Loader";

export interface ConsentIntegrationSectionProps {
    isInitiallyOpen?: boolean;
}

export enum ConsentIntegrationOptions {
    KLAVIYO = 'klaviyo',
    FLASHY = 'flashy',
    MAILCHIMP = 'mailchimp',
}

const ConsentIntegrationSection = ({ isInitiallyOpen }: ConsentIntegrationSectionProps) => {
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const { selectedDomain } = useUserContext();
    const [editForm, setEditForm] = useState<ConsentIntegrationOptions | null>(null);
    const [isKlaviyoConnected, setIsKlaviyoConnected] = useState(false);
    const [isMailchimpApiConnected, setIsMailchimpApiConnected] = useState(false);
    const [isMailchimpConnected, setIsMailchimpConnected] = useState(false);
    const [isLoadingKlaviyo, setIsLoadingKlaviyo] = useState(false);
    const [isLoadingMailchimp, setIsLoadingMailchimp] = useState(false);
    const [isLoadingFlashy, setIsLoadingFlashy] = useState(false);
    const [isFlashyConnected, setIsFlashyConnected] = useState(false);

    useEffect(() => {
        if(isInitiallyOpen) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isInitiallyOpen]);

    useEffect(() => {
        checkKlaviyoConsent();
        checkMailchimpConsent();
        checkFlashyConsent();
    }, []);

    const checkFlashyConsent = async () => {
        if(selectedDomain) {
            try {
                setIsLoadingFlashy(true);
                const isFlashyConnectedResponse = await consentIntegrationCheck(selectedDomain, 'flashy');
                if (isFlashyConnectedResponse.data === 'ok') {
                    setIsFlashyConnected(true);
                } else {
                    setIsFlashyConnected(false);
                }
                setIsLoadingFlashy(false);
            } catch(error) {
                setIsFlashyConnected(false);
                setIsLoadingFlashy(false);
            }
        }
    }

    const checkKlaviyoConsent = async () => {
        if(selectedDomain) {
            try {
                setIsLoadingKlaviyo(true);
                const isKlaviyoConnectedResponse = await consentIntegrationCheck(selectedDomain, 'klaviyo');
                if (isKlaviyoConnectedResponse.data === 'ok') {
                    setIsKlaviyoConnected(true);
                } else {
                    setIsKlaviyoConnected(false);
                }
                setIsLoadingKlaviyo(false);
            } catch(error) {
                setIsKlaviyoConnected(false);
                setIsLoadingKlaviyo(false);
            }
        }
    }

    const checkMailchimpConsent = async () => {
        if(selectedDomain) {
            try {
                setIsLoadingMailchimp(true);
                const isMailchimpApiConnectedResponse = await consentIntegrationCheck(selectedDomain, 'mailchimp');
                if (isMailchimpApiConnectedResponse.data === 'ok') {
                    setIsMailchimpApiConnected(true);
                } else {
                    setIsMailchimpApiConnected(false);
                }
            } catch (error) {
                setIsMailchimpApiConnected(false);
            }
            try {
                const isMailchimpListConnectedResponse = await consentMailchimpListCheck(selectedDomain);
                if (isMailchimpListConnectedResponse.data === 'ok') {
                    setIsMailchimpConnected(true);
                    setIsLoadingMailchimp(false);
                } else {
                    setIsMailchimpConnected(false);
                    setIsLoadingMailchimp(false);
                }
            } catch (error) {
                setIsMailchimpConnected(false);
                setIsLoadingMailchimp(false);
            }
        }
    }

    const closeEditForm = () => {
        setEditForm(null);
    }

    // console.log("isMailchimpApiConnected: ", isMailchimpApiConnected, isMailchimpConnected);

    return (
        <CollapsableBox title="Customers List Integration" isInitiallyOpen={isInitiallyOpen}>
            <div className="customers-list-integration-description">
                Connect your lists with Vuelve to automatically sync all your customers — including names, emails, phone numbers, country, and consent preferences — from popular marketing platforms like Klaviyo, Mailchimp, and Flashy.
                Vuelve keeps your customer list unified, accurate, and always up to date, so you can manage communications and compliance effortlessly from one dashboard.
            </div>
            <div ref={sectionRef} className="consent-integration-container">
                <div className="integration-list">
                    <div className="integration-item" onClick={() => setEditForm(ConsentIntegrationOptions.KLAVIYO)}>
                        <img src={KlaviyoLogo} alt="Klaviyo" />
                        <div className="title">Klaviyo</div>
                        {isLoadingKlaviyo ? <div className="loading-indicator"><Loader size={24} /></div> : null}
                        {isKlaviyoConnected ? <div className="success-indicator"><LinkOutlined /><span>Connected</span></div> : null}
                    </div>
                    <div className="integration-item" onClick={() => setEditForm(ConsentIntegrationOptions.MAILCHIMP)}>
                        <img src={MailchimpLogo} alt="Mailchimp" />
                        <div className="title">MailChimp</div>
                        {isLoadingMailchimp ? <div className="loading-indicator"><Loader size={24} /></div> : null}
                        {isMailchimpConnected ? <div className="success-indicator"><LinkOutlined /><span>Connected</span></div> : null}
                        {isMailchimpApiConnected && !isMailchimpConnected ? <div className="missing-indicator"><DisconnectOutlined /><span>Missing Key or List</span></div> : null}
                    </div>
                    <div className="integration-item" onClick={() => setEditForm(ConsentIntegrationOptions.FLASHY)}>
                        <img src={FlashyLogo} alt="Flashy" />
                        <div className="title">Flashy</div>
                        {isLoadingFlashy ? <div className="loading-indicator"><Loader size={24} /></div> : null}
                        {isFlashyConnected ? <div className="success-indicator"><LinkOutlined /><span>Connected</span></div> : null}
                    </div>
                    <div className="integration-item more" onClick={() => window.open("https://vuelve.co/contact-us")}>
                        <div className="more-title">Need to integrate more lists?</div>
                        <div className="title">Contact us</div>
                    </div>
                </div>
            </div>
            <ConsentIntegrationEditModal form={editForm} onClose={closeEditForm} isKlaviyoConnected={isKlaviyoConnected} setIsKlaviyoConnected={setIsKlaviyoConnected} isMailchimpConnected={isMailchimpConnected} setIsMailchimpConnected={setIsMailchimpConnected} isFlashyConnected={isFlashyConnected} setIsFlashyConnected={setIsFlashyConnected} />
        </CollapsableBox>
    );
}

export default ConsentIntegrationSection;